import { useState, useEffect } from 'react';
import { useSlices } from '../../hooks/useSlices';
import Modal from '../Modal';

const SLICE_PACKAGES = [
  { amount: 1, price: 2.5 },
  { amount: 3, price: 6.5 },
  { amount: 10, price: 20.0 },
  { amount: 30, price: 45.0 },
];

interface SliceBoxProps {
  onSliceOperation?: () => void;
}

export default function SliceBox({ onSliceOperation }: SliceBoxProps) {
  const { sliceBalance, loading, error, purchaseSlices, reloadBalance } =
    useSlices();
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<
    (typeof SLICE_PACKAGES)[0] | null
  >(null);
  const [purchasing, setPurchasing] = useState(false);



  // Listener per gli eventi di aggiornamento spicchi
  useEffect(() => {
    let isUpdating = false;  // Flag per prevenire aggiornamenti multipli
  
    const handleSliceUpdate = async () => {
      if (isUpdating) return;  // Se stiamo già aggiornando, esce
      
      try {
        isUpdating = true;
        console.log('SliceBox: Inizio aggiornamento saldo');
        await reloadBalance();
        console.log('SliceBox: Fine aggiornamento saldo');
      } finally {
        isUpdating = false;
      }
    };
  
    window.addEventListener('sliceBalanceUpdate', handleSliceUpdate);
    return () => {
      window.removeEventListener('sliceBalanceUpdate', handleSliceUpdate);
    };
  }, [reloadBalance]);

  const handlePurchase = async () => {
    if (!selectedPackage) return;
  
    try {
      setPurchasing(true);
      const purchased = await purchaseSlices(selectedPackage.amount);
      if (purchased) {
        window.dispatchEvent(new Event('sliceBalanceUpdate'));
        setShowPurchaseModal(false);
      }
    } catch (error) {
      console.error("Errore nell'acquisto degli spicchi:", error);
      alert("Si è verificato un errore durante l'acquisto.");
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) {
    return <div>Caricamento...</div>;
  }

  if (error) {
    return <div className="text-red-600">Errore: {error}</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <img
            src="https://pizzaveg.it/materiale/spicchio.png"
            alt="Spicchio"
            className="w-8 h-8"
          />
          <div>
            <h3 className="text-lg font-semibold">Il tuo saldo spicchi</h3>
            <p className="text-2xl font-bold text-pizzaveg">{sliceBalance}</p>
          </div>
        </div>
        <button
          onClick={() => setShowPurchaseModal(true)}
          className="bg-pizzaveg hover:bg-pizzaveg-dark text-white px-4 py-2 rounded-lg transition-colors"
        >
          Acquista spicchi
        </button>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center space-x-4 mb-4">
          <img
            src="https://pizzaveg.it/materiale/box-spicchi.jpg"
            alt="Box Spicchi"
            className="w-16 h-16 rounded-lg"
          />
          <div>
            <h4 className="font-medium">Come funzionano gli spicchi?</h4>
            <p className="text-sm text-gray-600">
              Uno spicchio ti permette di aggiungere una pizza al menu per 30
              giorni.
            </p>
          </div>
        </div>
      </div>

      {showPurchaseModal && (
        <Modal onClose={() => setShowPurchaseModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Acquista spicchi</h3>
            <div className="space-y-4">
              {SLICE_PACKAGES.map((pkg) => (
                <button
                  key={pkg.amount}
                  onClick={() => setSelectedPackage(pkg)}
                  className={`w-full p-4 rounded-lg border-2 transition-colors ${
                    selectedPackage === pkg
                      ? 'border-pizzaveg bg-pizzaveg bg-opacity-10'
                      : 'border-gray-200 hover:border-pizzaveg'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="font-semibold">
                        {pkg.amount} spicchi
                      </span>
                    </div>
                    <div className="text-lg font-bold">
                      € {pkg.price.toFixed(2)}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={() => setShowPurchaseModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annulla
              </button>
              <button
                onClick={handlePurchase}
                disabled={!selectedPackage || purchasing}
                className="bg-pizzaveg hover:bg-pizzaveg-dark text-white px-6 py-2 rounded-lg disabled:opacity-50"
              >
                {purchasing ? 'Acquisto in corso...' : 'Acquista'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}